// PlanModal.jsx
import { useState, useEffect } from 'react';
import { planApi } from '../utils/api';
import { X, CheckCircle, XCircle, Zap, ArrowRight } from 'lucide-react';
import toast from 'react-hot-toast';

const FEATURE_LABELS = {
  bookings:    'Réservations',
  timer:       'Timer salles',
  cash:        'Caisse',
  reports:     'Rapports',
  schedule:    'Planning RH',
  stock:       'Stock & Vente',
  customers:   'Clients',
  multi_site:  'Multi-sites',
  max_rooms:   'Salles',
  permissions: 'Droits avancés',
  api_access:  'Accès API',
  white_label: 'White label',
};

const PLAN_COLORS = {
  starter:    { bg:'rgba(245,166,35,0.1)',  border:'rgba(245,166,35,0.3)',  color:'#f5a623' },
  pro:        { bg:'rgba(0,210,180,0.1)',   border:'rgba(0,210,180,0.3)',   color:'#00d2b4' },
  enterprise: { bg:'rgba(124,92,252,0.1)', border:'rgba(124,92,252,0.3)', color:'#a387fd' },
};

export default function PlanModal({ onClose }) {
  const [current, setCurrent]   = useState(null);
  const [plans, setPlans]       = useState([]);
  const [requesting, setReq]    = useState(null); // plan selectionné pour changement
  const [message, setMessage]   = useState('');
  const [saving, setSaving]     = useState(false);
  const [requests, setRequests] = useState([]);

  useEffect(() => {
    Promise.all([
      planApi.current(),
      planApi.available(),
      planApi.requests().catch(() => ({ data: { requests: [] } })),
    ]).then(([c, a, r]) => {
      setCurrent(c.data.plan);
      setPlans(a.data.plans);
      setRequests(r.data.requests || []);
    });
  }, []);

  const sendRequest = async () => {
    if (!requesting) return;
    setSaving(true);
    try {
      await planApi.requestChange({ toPlanId: requesting.id, message });
      toast.success('Demande envoyée — notre équipe vous contactera');
      setReq(null);
      setMessage('');
      planApi.requests().then(r => setRequests(r.data.requests || []));
    } catch(e) { toast.error(e.response?.data?.error || 'Erreur'); }
    finally { setSaving(false); }
  };

  const pendingRequest = requests.find(r => r.status === 'pending');
  const currentFeatures = (current?.features || []).filter(f => f.feature);

  return (
    <div className="modal-overlay" onClick={e => e.target===e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth:680 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
          <h3 style={{ margin:0, fontSize:18 }}>Mon abonnement</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-3)' }}>
            <X size={20}/>
          </button>
        </div>

        {/* Plan actuel */}
        {current && (() => {
          const colors = PLAN_COLORS[current.plan_name] || PLAN_COLORS.starter;
          return (
            <div style={{ padding:'16px 20px', borderRadius:12, marginBottom:20,
              background: colors.bg, border:`1px solid ${colors.border}` }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
                <div>
                  <div style={{ fontSize:11, fontWeight:700, textTransform:'uppercase',
                    letterSpacing:'0.08em', color: colors.color, marginBottom:4 }}>Plan actuel</div>
                  <div style={{ fontSize:22, fontWeight:800, color:'var(--text-1)' }}>{current.plan_label}</div>
                </div>
                <div style={{ textAlign:'right' }}>
                  <div style={{ fontSize:28, fontWeight:800, color: colors.color }}>
                    {current.price_month} €<span style={{ fontSize:14, fontWeight:400 }}>/mois</span>
                  </div>
                  <div style={{ fontSize:12, color:'var(--text-3)' }}>ou {current.price_year} €/an</div>
                </div>
              </div>
              <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                {currentFeatures.map(f => (
                  <div key={f.feature} style={{ display:'flex', alignItems:'center', gap:4,
                    padding:'3px 10px', borderRadius:20, fontSize:12, fontWeight:500,
                    background: f.enabled ? 'rgba(0,210,180,0.1)' : 'rgba(255,77,109,0.1)',
                    color: f.enabled ? 'var(--teal)' : 'var(--text-3)' }}>
                    {f.enabled ? <CheckCircle size={11}/> : <XCircle size={11}/>}
                    {FEATURE_LABELS[f.feature] || f.feature}
                    {f.limit && ` (max ${f.limit})`}
                  </div>
                ))}
              </div>
              {current.trial_ends_at && new Date(current.trial_ends_at) > new Date() && (
                <div style={{ marginTop:10, fontSize:12, color:'var(--amber)' }}>
                  ⏱ Période d'essai jusqu'au {new Date(current.trial_ends_at).toLocaleDateString('fr-FR')}
                </div>
              )}
            </div>
          );
        })()}

        {/* Demande en cours */}
        {pendingRequest && (
          <div style={{ padding:'10px 14px', borderRadius:8, marginBottom:16,
            background:'rgba(245,166,35,0.08)', border:'1px solid rgba(245,166,35,0.3)',
            fontSize:13, color:'var(--amber)' }}>
            ⏳ Demande de passage en <strong>{pendingRequest.to_plan_label}</strong> en cours de traitement
          </div>
        )}

        {/* Comparatif des plans */}
        {!requesting && !pendingRequest && (
          <>
            <div style={{ fontSize:13, fontWeight:600, color:'var(--text-2)', marginBottom:12 }}>
              Changer de plan
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:20 }}>
              {plans.map(plan => {
                const isCurrent = plan.id === current?.plan_id;
                const colors = PLAN_COLORS[plan.name] || PLAN_COLORS.starter;
                return (
                  <div key={plan.id} style={{ padding:16, borderRadius:12,
                    border:`1px solid ${isCurrent ? colors.border : 'var(--border)'}`,
                    background: isCurrent ? colors.bg : 'var(--bg-elevated)',
                    position:'relative' }}>
                    {isCurrent && (
                      <div style={{ position:'absolute', top:-10, left:'50%', transform:'translateX(-50%)',
                        background: colors.color, color:'#fff', fontSize:10, fontWeight:700,
                        padding:'2px 10px', borderRadius:20, whiteSpace:'nowrap' }}>
                        Actuel
                      </div>
                    )}
                    <div style={{ fontWeight:700, fontSize:15, marginBottom:4 }}>{plan.label}</div>
                    <div style={{ fontSize:20, fontWeight:800, color: colors.color, marginBottom:12 }}>
                      {plan.price_month} €<span style={{ fontSize:12, color:'var(--text-3)', fontWeight:400 }}>/mois</span>
                    </div>
                    <div style={{ display:'flex', flexDirection:'column', gap:4, marginBottom:14 }}>
                      {(plan.features||[]).map(f => (
                        <div key={f.feature} style={{ display:'flex', alignItems:'center', gap:6, fontSize:12 }}>
                          {f.enabled
                            ? <CheckCircle size={11} color="var(--teal)"/>
                            : <XCircle size={11} color="var(--text-3)"/>}
                          <span style={{ color: f.enabled ? 'var(--text-1)' : 'var(--text-3)' }}>
                            {FEATURE_LABELS[f.feature] || f.feature}
                          </span>
                        </div>
                      ))}
                    </div>
                    {!isCurrent && (
                      <button onClick={() => setReq(plan)}
                        style={{ width:'100%', padding:'7px', borderRadius:8, border:'none',
                          background: colors.color, color:'#fff', fontSize:12, fontWeight:600,
                          cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
                        Choisir <ArrowRight size={12}/>
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Formulaire demande */}
        {requesting && (
          <div style={{ padding:20, borderRadius:12, background:'var(--bg-elevated)', border:'1px solid var(--border)' }}>
            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:16 }}>
              <Zap size={18} color="var(--accent-light)"/>
              <div>
                <div style={{ fontWeight:700 }}>Passer en {requesting.label}</div>
                <div style={{ fontSize:13, color:'var(--text-3)' }}>
                  Notre équipe vous contactera pour finaliser le changement
                </div>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Message (optionnel)</label>
              <textarea className="input" rows={3} value={message}
                onChange={e => setMessage(e.target.value)}
                placeholder="Besoins particuliers, question sur le plan…"
                style={{ resize:'vertical' }}/>
            </div>
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setReq(null)}>Annuler</button>
              <button className="btn btn-primary" onClick={sendRequest} disabled={saving}>
                {saving ? <span className="spinner" style={{width:14,height:14}}/> : 'Envoyer la demande'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
